/**
 * The variant CSV (MERCH-88): one row per variant (a size / colour), grouped into products by `handle`.
 * Pure. The client reads the file into rows and checks the header; the server
 * (lib/catalog/importVariants, InventoryUnit importVariants) checks every row and imports all or nothing,
 * answering 422 with problems by row (path `rows.<row>.<field>`).
 */
import { rupeesToPaise } from './money';
import { attributesFromForm } from '@/lib/productTypes';

// Option columns are named by the option's key, as the server reads them (lib/catalog/variants optionKey).
export const optionKey = (name) => String(name || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/** Does this type take the variant CSV (it has options and is sold by quantity)? */
export const takesVariants = (type) => Boolean(type && Array.isArray(type.options) && type.options.length && type.stock === 'quantity');

/** { required, optional } columns of a type's variant CSV. */
export function variantColumns(type) {
  if (!type) return { required: [], optional: [] };
  const options = (type.options || []).map((o) => optionKey(o.name));
  const swatches = (type.options || []).filter((o) => o.style === 'swatch').map((o) => `${optionKey(o.name)}_swatch`);
  const attrs = (type.attributes || []).map((a) => a.key);
  return {
    required: ['handle', 'brand', 'model_name', 'condition', ...options, 'sku', 'price'],
    optional: ['series', 'grade', 'warranty_months', 'barcode', 'mrp', 'qty', ...swatches, ...attrs],
  };
}

const cell = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));

/** A template for a type: its header, then its registry `variant_example` rows (else one blank-ish row). */
export function variantTemplate(type) {
  if (!takesVariants(type)) return '';
  const cols = variantColumns(type);
  const header = [...cols.required, ...cols.optional];
  const rows = Array.isArray(type.variant_example) && type.variant_example.length ? type.variant_example : [{ handle: 'my-product', qty: '1' }];
  return [header.join(','), ...rows.map((r) => header.map((h) => cell(r[h] ?? '')).join(','))].join('\n');
}

/**
 * A parsed CSV table (parseCsv) → { headerErrors, rows: [payload], lines } for the server. Prices are rupees
 * in the file, paise on the wire; specs go in `attributes`. The server re-checks everything.
 */
export function variantRows(table, type) {
  const [head = [], ...body] = table || [];
  const header = head.map((h) => String(h).trim().toLowerCase());
  const cols = variantColumns(type);
  const missing = cols.required.filter((c) => !header.includes(c));
  const headerErrors = missing.length ? [`Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`] : [];
  const rows = body.filter((r) => r.some((c) => String(c).trim() !== '')).map((r) => {
    const raw = Object.fromEntries(header.map((h, i) => [h, String(r[i] ?? '').trim()]));
    const out = { product_type: type.key, attributes: attributesFromForm(type, raw) };
    for (const k of ['handle', 'brand', 'series', 'model_name', 'condition', 'grade', 'sku', 'barcode']) if (raw[k]) out[k] = raw[k];
    for (const o of type.options || []) {
      const key = optionKey(o.name);
      if (raw[key]) out[key] = raw[key];
      if (raw[`${key}_swatch`]) out[`${key}_swatch`] = raw[`${key}_swatch`];
    }
    if (raw.price) out.price_paise = rupeesToPaise(raw.price) ?? raw.price;
    if (raw.mrp) out.mrp_paise = rupeesToPaise(raw.mrp) ?? raw.mrp;
    if (raw.qty) out.qty = /^\d+$/.test(raw.qty) ? Number(raw.qty) : raw.qty;
    if (raw.warranty_months) out.warranty_months = /^\d+$/.test(raw.warranty_months) ? Number(raw.warranty_months) : raw.warranty_months;
    return out;
  });
  return { headerErrors, rows };
}

/** The server's 422 details → { [row]: [message…] } (row 0: the file). */
export function rowProblems(details) {
  const out = {};
  for (const d of details || []) {
    const m = /^rows\.(\d+)(?:\.(.+))?$/.exec(String(d.path || ''));
    const row = m ? Number(m[1]) : 0;
    (out[row] = out[row] || []).push(d.message);
  }
  return out;
}
